import { Toast } from '@components/Toast/Toast';
import { BackTo } from '@components/util/BackToPage';
import { SubtreeErrorBoundary } from '@components/util/SubtreeErrorBoundary';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { FolderPath, resolveFolderPath } from '@lib/library/folderPath';
import { ModalTab } from '@lib/media/modalTab';
import { getLibraryUrl, getMediaUrl } from '@lib/utils/url';
import { AppPage } from '@pages/AppPage';
import { MomentModal } from '@pages/Search/components/MomentGrid/components/MomentModal/MomentModal';
import { LibraryProvider } from '@stores/Library/Library';
import { LibraryFolderProvider, useLibraryFolder } from '@stores/Library/LibraryFolder';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { MomentMergeProvider } from '@stores/MomentMerge/MomentMergeStore';
import { Renderer } from '@stores/Renderer/Renderer';
import { trackingStore } from '@stores/Tracking';
import { useUIPreferences } from '@stores/UIPreferences';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LibraryContent } from './LibraryContent';
import pageCss from './LibraryPage.module.scss';
import {
  FolderDialogsHost,
  FolderDialogsProvider,
  OpenFolderDialogFn,
} from './components/Folders/FolderDialogs';
import {
  MediaDeleteModalHost,
  MediaDeleteModalProvider,
  OpenDeleteModalFn,
} from './context/MediaDeleteModalContext';
import { MomentModalProvider, useMomentModal } from './context/MomentModalContext';

const MomentModalWrapper = observer(function MomentModalWrapper({
  mediaId,
  tab,
  startAt,
  commentId,
}: {
  mediaId?: string;
  tab: ModalTab;
  startAt?: number;
  commentId?: string;
}) {
  const { currentMedia, closeModal, isOpen, openModal } = useMomentModal();
  const folder = useLibraryFolder();
  const navigate = useNavigate();
  const location = useLocation();
  const uiPreferences = useUIPreferences();

  // Release the provider's `isOpen` if this component goes away without anyone having
  // called `closeModal` — which is precisely what happens when the surrounding error
  // boundary catches and unmounts it.
  //
  // A stuck `isOpen` is not a cosmetic leak: `VideoCard` passes it to
  // `useDesktopHoverPlay` as `suspended`, so it silently kills hover previews on EVERY
  // card in the library. That used to be invisible because a modal crash took the whole
  // page down with it; now that the grid survives, the leak is what the user would see.
  //
  // Via a ref so the cleanup runs only on unmount and always calls the current closure
  // (see `.claude/rules/stable-callbacks.md`).
  const closeModalRef = useRef(closeModal);
  closeModalRef.current = closeModal;
  useEffect(() => () => closeModalRef.current(), []);

  const handleTabChange = (newTab: ModalTab) => {
    uiPreferences.setMomentModalDefaultTab(newTab);

    if (mediaId && currentMedia) {
      const url = getMediaUrl(folder.library.libraryId, mediaId, {
        libraryName: folder.library.name,
        mediaName: currentMedia.metadata?.name,
      });
      // Rebuild the query from the current one rather than writing `?tab=` alone. This used
      // to drop every other param, which was invisible while `tab` was the only one — then
      // `?t=` arrived, and switching tabs would have silently stripped the timestamp out of
      // the address bar the user is about to copy.
      const params = new URLSearchParams(location.search);
      params.set('tab', newTab);
      navigate(`${url}?${params.toString()}`, { replace: true });
    }
  };

  const handleModalClose = () => {
    // Back to the library, in the folder holding this media — the one the user opened it from,
    // or, for a link straight to the media, the one it lives in. Read from the store rather than
    // `currentMedia`, which is the copy the modal opened with: a "Move to…" from inside the
    // modal has filed the media somewhere else since.
    if (mediaId) {
      const media = folder.allFiles.find((file) => file._id === mediaId) ?? currentMedia;
      navigate(
        getLibraryUrl(folder.library.libraryId, folder.library.name, {
          folderPath: media ? folder.folderOf(media) : undefined,
        }),
      );
    }
    closeModal();
  };

  // Effect to open modal for specific mediaId when component mounts
  useEffect(() => {
    if (mediaId && folder.allFiles.length > 0) {
      const media = folder.allFiles.find((file) => file._id === mediaId);
      if (media) {
        openModal(media);
      } else {
        toast.custom((t) => (
          <Toast
            header="Media not found"
            description={`The requested media could not be found in this library. Please check if you have the right URL. In the meantime, you have been redirected to the ${folder.library.name} library page.`}
            variant="error"
          />
        ));
        navigate(getLibraryUrl(folder.library.libraryId, folder.library.name), { replace: true });
      }
    }
  }, [mediaId, folder.allFiles, openModal]);

  // Modal should be open if we have a mediaId and currentMedia
  const shouldShowModal = Boolean(mediaId && currentMedia);

  if (!shouldShowModal || !currentMedia) return null;

  return (
    <MomentMergeProvider arguments={[new Renderer()]}>
      <MomentModal
        variant="library-media-card"
        media={currentMedia}
        onClose={handleModalClose}
        open={shouldShowModal}
        tab={tab}
        onTabChange={handleTabChange}
        startAt={startAt}
        commentId={commentId}
      />
    </MomentMergeProvider>
  );
});

/**
 * Puts the store in the folder the URL names — the only writer of `currentFolderPath`.
 *
 * Two links are corrected in place once the tree has loaded: one naming a folder the library no
 * longer has (renamed, moved or deleted since it was copied) opens the nearest one that remains,
 * and a `?mediaId=` highlight opens the folder holding that media, since the grid can only
 * scroll to what it shows. Neither fires while the listing is absent — a failed fetch says
 * nothing about which folders exist.
 */
const FolderRouteSync = observer(function FolderRouteSync({
  folderPath,
}: {
  folderPath?: FolderPath;
}) {
  const folder = useLibraryFolder();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loaded = folder.data !== null;
  const highlightId = searchParams.get('mediaId');

  useEffect(() => {
    if (folderPath === undefined) return;
    folder.setCurrentFolderPath(folderPath);
    if (!loaded) return;

    const highlighted = highlightId
      ? folder.allFiles.find((file) => file._id === highlightId)
      : undefined;
    const target = highlighted
      ? folder.folderOf(highlighted)
      : resolveFolderPath(folderPath, folder.folderPaths);
    if (target === folderPath) return;

    navigate(
      getLibraryUrl(folder.library.libraryId, folder.library.name, {
        folderPath: target,
        highlightMediaId: highlighted?._id,
      }),
      { replace: true },
    );
  }, [folder, folderPath, loaded, folder.folderPaths, folder.allFiles, highlightId]);

  return null;
});

const SelectedLibraryContext = observer(function LibraryWithContext({
  id,
  mediaId,
  tab,
  startAt,
  commentId,
  folderPath,
  children,
}: {
  id: string;
  mediaId?: string;
  tab: ModalTab;
  startAt?: number;
  commentId?: string;
  folderPath?: FolderPath;
  children: React.ReactNode;
}) {
  const manager = useLibraryManager();
  const selectedLibrary = manager._libraries.get(id);
  const mediaDeleteModalOpenRef = useRef<OpenDeleteModalFn | null>(null);
  const folderDialogsOpenRef = useRef<OpenFolderDialogFn | null>(null);
  const uiPreferences = useUIPreferences();
  const uploads = useUploadManager();
  const location = useLocation();

  // Read lazily, at catch time, so the report describes the state that broke.
  // `rejectedFileCount` is here on purpose: the crash this boundary was split for
  // fired when retrying failed uploads emptied that list, so knowing whether a
  // retry had just happened is the first question worth answering.
  const getLibraryExtra = useCallback(
    () => ({
      libraryId: id,
      libraryName: selectedLibrary?.name,
      viewMode: uiPreferences.libraryViewMode,
      rejectedFileCount: uploads.rejectedFiles.length,
      mediaId: mediaId ?? null,
      tab,
    }),
    [id, selectedLibrary, uiPreferences, uploads, mediaId, tab],
  );

  const getModalExtra = useCallback(
    () => ({
      libraryId: id,
      libraryName: selectedLibrary?.name,
      mediaId: mediaId ?? null,
      tab,
    }),
    [id, selectedLibrary, mediaId, tab],
  );

  if (!selectedLibrary) return null;

  return (
    <LibraryProvider staticStore={selectedLibrary}>
      <LibraryFolderProvider arguments={[selectedLibrary]}>
        <FolderRouteSync folderPath={folderPath} />
        <MediaDeleteModalProvider openRef={mediaDeleteModalOpenRef}>
          <FolderDialogsProvider openRef={folderDialogsOpenRef}>
            <MomentModalProvider>
              {/* Two boundaries, deliberately. A single one used to wrap both of these
                and was named for the modal, so a crash anywhere in the library page
                (header, grid, table) was reported as a modal failure AND replaced the
                whole page with the modal's error card. Now the grid survives a modal
                crash, the modal survives a grid crash, and Sentry gets the right name. */}
              {/* `resetKeys` on both: `/library/:id` and `/library/:id/media/:mediaId` are
                sibling routes rendering the same element, so React reuses these boundary
                instances across navigation and a stale `hasError` would otherwise follow
                the user to a different library or a different media item. */}
              <SubtreeErrorBoundary
                name="LibraryContent"
                getExtra={getLibraryExtra}
                resetKeys={[id]}
              >
                {children}
              </SubtreeErrorBoundary>
              <SubtreeErrorBoundary
                name="MomentModal"
                getExtra={getModalExtra}
                // The modal is an overlay: a fallback panel would render behind nothing
                // and be unreachable. Dropping it is the correct degradation — the toast
                // still fires and the page underneath stays usable.
                //
                // But it makes `resetKeys` load-bearing rather than a nicety: with no
                // fallback there is no "Try again" button, so navigating is the ONLY way
                // back. Without the reset the modal would stay dead for the rest of the
                // page's life, showing nothing to explain itself.
                //
                // `location.key` and not just `mediaId`: after a crash the URL still points
                // at the failed media, so clicking that same card navigates to an identical
                // path and leaves `mediaId` unchanged — the boundary wouldn't reset and the
                // user would get no modal, no fallback and no repeat toast. Clicking the
                // thing again is the obvious recovery action and it must not be a silent
                // no-op. `key` is fresh per history entry, so it covers same-path clicks
                // too. Harmless in the happy path: `resetKeys` is only consulted while the
                // boundary is already in its error state.
                fallback={null}
                resetKeys={[mediaId, location.key]}
              >
                <MomentModalWrapper
                  mediaId={mediaId}
                  tab={tab}
                  startAt={startAt}
                  commentId={commentId}
                />
              </SubtreeErrorBoundary>
            </MomentModalProvider>
          </FolderDialogsProvider>
        </MediaDeleteModalProvider>
        <MediaDeleteModalHost openRef={mediaDeleteModalOpenRef} />
        <FolderDialogsHost openRef={folderDialogsOpenRef} />
      </LibraryFolderProvider>
    </LibraryProvider>
  );
});

export const LibraryPage = observer(function LibraryPage({
  id,
  mediaId,
  tab,
  startAt,
  commentId,
  folderPath,
}: {
  id: string;
  mediaId?: string;
  tab: ModalTab;
  /** Playhead position in seconds from `?t=`, when the link carried one. */
  startAt?: number;
  /** The comment a comment link (`?comment=`) points to, to be selected once comments load. */
  commentId?: string;
  /** The folder `?folder=` opens; undefined on the media route, which keeps the grid's folder. */
  folderPath?: FolderPath;
}) {
  const isMobile = useIsMobile();

  useEffect(() => {
    trackingStore.libraryViewed({ libraryId: id });
  }, [id]);

  return (
    <AppPage
      header={<BackTo href="/libraries">Back to all libraries</BackTo>}
      className={isMobile ? pageCss.mobileAppPage : undefined}
    >
      <SelectedLibraryContext
        id={id}
        mediaId={mediaId}
        tab={tab}
        startAt={startAt}
        commentId={commentId}
        folderPath={folderPath}
      >
        <LibraryContent />
      </SelectedLibraryContext>
    </AppPage>
  );
});
